import { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Page,
  Card,
  FormLayout,
  TextField,
  Button,
  Banner,
  BlockStack,
  Text,
  SkeletonBodyText,
  Divider,
} from '@shopify/polaris';
import { clinicPortalApi } from '@/services';
import type { ClinicProfile as ClinicProfileType } from '@/services/clinicPortal';

export default function ClinicProfile() {
  const queryClient = useQueryClient();

  const { data: profile, isLoading, error } = useQuery({
    queryKey: ['clinic-profile'],
    queryFn: () => clinicPortalApi.getProfile(),
  });

  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [address1, setAddress1] = useState('');
  const [city, setCity] = useState('');
  const [province, setProvince] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [country, setCountry] = useState('');
  const [billingAddress1, setBillingAddress1] = useState('');
  const [billingCity, setBillingCity] = useState('');
  const [billingProvince, setBillingProvince] = useState('');
  const [billingPostalCode, setBillingPostalCode] = useState('');
  const [billingCountry, setBillingCountry] = useState('');

  useEffect(() => {
    if (profile) {
      setEmail(profile.email || '');
      setPhone(profile.phone || '');
      setAddress1(profile.address1 || '');
      setCity(profile.city || '');
      setProvince(profile.province || '');
      setPostalCode(profile.postalCode || '');
      setCountry(profile.country || '');
      setBillingAddress1(profile.billingAddress1 || '');
      setBillingCity(profile.billingCity || '');
      setBillingProvince(profile.billingProvince || '');
      setBillingPostalCode(profile.billingPostalCode || '');
      setBillingCountry(profile.billingCountry || '');
    }
  }, [profile]);

  const mutation = useMutation({
    mutationFn: (data: Partial<ClinicProfileType>) => clinicPortalApi.updateProfile(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clinic-profile'] });
    },
  });

  const handleSave = useCallback(() => {
    mutation.mutate({
      email: email || null,
      phone: phone || null,
      address1: address1 || null,
      city: city || null,
      province: province || null,
      postalCode: postalCode || null,
      country: country || null,
      billingAddress1: billingAddress1 || null,
      billingCity: billingCity || null,
      billingProvince: billingProvince || null,
      billingPostalCode: billingPostalCode || null,
      billingCountry: billingCountry || null,
    });
  }, [email, phone, address1, city, province, postalCode, country, billingAddress1, billingCity, billingProvince, billingPostalCode, billingCountry, mutation]);

  if (isLoading) {
    return (
      <Page title="Clinic Profile">
        <Card><SkeletonBodyText lines={10} /></Card>
      </Page>
    );
  }

  if (error) {
    return (
      <Page title="Clinic Profile">
        <Banner tone="critical">
          <p>Failed to load profile. Please try again.</p>
        </Banner>
      </Page>
    );
  }

  return (
    <Page title="Clinic Profile">
      <BlockStack gap="500">
        {mutation.isSuccess && (
          <Banner tone="success">
            <p>Profile updated successfully.</p>
          </Banner>
        )}

        {mutation.isError && (
          <Banner tone="critical">
            <p>Failed to update profile. Please try again.</p>
          </Banner>
        )}

        <Card>
          <BlockStack gap="400">
            <Text as="h2" variant="headingMd">General Information</Text>
            <FormLayout>
              <TextField
                label="Clinic Name"
                value={profile?.name || ''}
                onChange={() => {}}
                disabled
                autoComplete="organization"
              />
              <FormLayout.Group>
                <TextField
                  label="Email"
                  type="email"
                  value={email}
                  onChange={setEmail}
                  autoComplete="email"
                />
                <TextField
                  label="Phone"
                  type="tel"
                  value={phone}
                  onChange={setPhone}
                  autoComplete="tel"
                />
              </FormLayout.Group>
            </FormLayout>
          </BlockStack>
        </Card>

        <Card>
          <BlockStack gap="400">
            <Text as="h2" variant="headingMd">Shipping Address</Text>
            <FormLayout>
              <TextField
                label="Address"
                value={address1}
                onChange={setAddress1}
                autoComplete="address-line1"
              />
              <FormLayout.Group>
                <TextField
                  label="City"
                  value={city}
                  onChange={setCity}
                  autoComplete="address-level2"
                />
                <TextField
                  label="Province"
                  value={province}
                  onChange={setProvince}
                  autoComplete="address-level1"
                />
              </FormLayout.Group>
              <FormLayout.Group>
                <TextField
                  label="Postal Code"
                  value={postalCode}
                  onChange={setPostalCode}
                  autoComplete="postal-code"
                />
                <TextField
                  label="Country"
                  value={country}
                  onChange={setCountry}
                  autoComplete="country-name"
                />
              </FormLayout.Group>
            </FormLayout>
          </BlockStack>
        </Card>

        <Card>
          <BlockStack gap="400">
            <Text as="h2" variant="headingMd">Billing Address</Text>
            <FormLayout>
              <TextField
                label="Address"
                value={billingAddress1}
                onChange={setBillingAddress1}
                autoComplete="address-line1"
              />
              <FormLayout.Group>
                <TextField
                  label="City"
                  value={billingCity}
                  onChange={setBillingCity}
                  autoComplete="address-level2"
                />
                <TextField
                  label="Province"
                  value={billingProvince}
                  onChange={setBillingProvince}
                  autoComplete="address-level1"
                />
              </FormLayout.Group>
              <FormLayout.Group>
                <TextField
                  label="Postal Code"
                  value={billingPostalCode}
                  onChange={setBillingPostalCode}
                  autoComplete="postal-code"
                />
                <TextField
                  label="Country"
                  value={billingCountry}
                  onChange={setBillingCountry}
                  autoComplete="country-name"
                />
              </FormLayout.Group>
            </FormLayout>
          </BlockStack>
        </Card>

        <Divider />

        <Button
          variant="primary"
          onClick={handleSave}
          loading={mutation.isPending}
        >
          Save Changes
        </Button>
      </BlockStack>
    </Page>
  );
}
